// @flow

import React, { type Node, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useScreenWidth } from '@tectonics/hooks';
import { handleError } from '@tectonics/alert-handler';
import { Text } from '../../../../../atoms';
import { getDebitCardBrandsForCountry, getDebitCardBrandImageSrc } from '../../commons/debit-card-brands';

export function DisplayBrazilianDebitcardBanksInfo(): Node {
  const screenWidth = useScreenWidth();
  const debitCardBrands = useMemo(() => getDebitCardBrandsForCountry('br'), []);
  const [bankListExpanded, setBankListExpanded] = useState<boolean>(false);

  const shouldCollapseBankList = useMemo(() => screenWidth < 450 && !bankListExpanded, [screenWidth, bankListExpanded]);

  return (
    <DebitCardBanksInfo data-test-id="debitcard-banks-info">
      <Text flavor="secondary">Os bancos e bandeiras aceitos nesse método de pagamento são:</Text>

      <DebitCardBrands>
        {debitCardBrands.map(brand => (
          <CardBrandImageWrapper key={brand}>
            <CardBrandImage alt={brand} src={getDebitCardBrandImageSrc(brand)} />
          </CardBrandImageWrapper>
        ))}
      </DebitCardBrands>

      <DebitCardsBanksList collapsed={shouldCollapseBankList}>
        {debitCardBrands.map(brand => (
          <DebitCardBankListItem key={brand}>{getDebitCardBankByBrand(brand)}</DebitCardBankListItem>
        ))}
      </DebitCardsBanksList>

      {shouldCollapseBankList && (
        <>
          <Hr />
          <Text
            center
            flavor="secondary"
            style={{ cursor: 'pointer' }}
            onClick={() => setBankListExpanded(true)}
          >
            Ver lista completa
          </Text>
        </>
      )}
    </DebitCardBanksInfo>
  );
}

const DebitCardBanksInfo = styled.div`
  padding: 14px;
  font-size: ${props => props.theme.fontSize};
  color: ${props => props.theme.colors.neutral[900]};
  font-family: ${props => props.theme.fontFamily.sansSerif};
  background-color: ${props => props.theme.colors.new_primary[50]};
`;

const DebitCardBrands = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  width: 100%;
  margin: 10px 0 14px 0;
`;

const CardBrandImageWrapper = styled.div`
  height: 20px;
  margin-right: 6px;
  margin-bottom: 6px;
`;

const CardBrandImage = styled.img`
  height: 100%;
  width: 100%;
`;

const DebitCardsBanksList = styled.ul`
  columns: 1;
  font-size: 12px;
  list-style-position: inside;
  overflow: hidden;

  li:nth-child(n+6) {
    display: ${props => props.collapsed ? 'none' : ''};
  }

  @media (min-width: 450px) {
    columns: 2;
  }
`;

const DebitCardBankListItem = styled.li`
  width: 100%;
  margin-bottom: 4px;
`;

const Hr = styled.hr`
  border: 0;
  margin: 1em 0;
  border-top: 1px solid rgba(50, 69, 105, 0.1);
`;

function getDebitCardBankByBrand(cardBrand: string): string {
  switch (cardBrand) {
    case 'nubank':
      return 'Nubank: Mastercard';
    case 'santander':
      return 'Santander: Visa e Mastercard';
    case 'bancodobrasil':
      return 'Banco do Brasil: Visa e Elo';
    case 'caixa':
      return 'Caixa: Elo Débito Virtual';
    case 'bradesco':
      return 'Bradesco: Visa e Elo';
    case 'original':
      return 'Original: Mastercard';
    case 'inter':
      return 'Inter: Mastercard';
    case 'c6':
      return 'C6: Mastercard';
    case 'bancoob':
      return 'Bancoob: Mastercard';
    case 'bancopan':
      return 'Banco PAN: Mastercard';
    case 'sicredi':
      return 'Sicredi: Visa';
    case 'agibank':
      return 'Agibank: Mastercard';
    case 'bancobmg':
      return 'Banco BMG: Mastercard';
    case 'brb':
      return 'BRB: Mastercard';
    case 'itau':
      return 'Itau: Visa e Mastercard';

    default: {
      handleError(new Error(`DisplayBrazilianDebitcardBanksInfo - getDebitCardBankByBrand - there is no bank for "${cardBrand}"`));
      return '';
    }
  }
}
